/**
 * Keeping a fixed-position overlay inside the box its anchor lives in.
 *
 * `SyntaxHighlightInput` expands a too-long single line into a multi-line
 * overlay portaled to `<body>` and positioned `fixed` at the anchor's viewport
 * rect. That is fine for the URL bar, which is as wide as the window — and
 * wrong for a params/headers cell, which sits in a table that SCROLLS
 * horizontally when the window is narrow. Two things went visibly wrong there
 * (owner, 22.09.2026): the overlay kept the coordinates it was given when it
 * opened, so scrolling the table left it stranded over the header row; and it
 * was drawn at the anchor's full width, so it spilled past the panel edge
 * instead of being clipped like the cell underneath it.
 *
 * A portal cannot inherit the container's clipping, so the clipping is computed
 * here. The geometry is a pure function on purpose: jsdom reports every rect as
 * zero, so the only way to test this honestly is to hand it the rectangles.
 */

/** Rect shape used throughout: what `getBoundingClientRect()` returns. */

/**
 * The nearest ancestor that would clip `el` — the box the overlay must stay in.
 *
 * Walks up looking for a scroll container. Stops at `<body>`, whose rect is the
 * document rather than the viewport and would clip nothing useful.
 *
 * @param {Element|null} el
 * @returns {Element|null} null when nothing clips it (the URL bar's case).
 */
export function findClipAncestor(el) {
  if (!el || typeof window === 'undefined') return null;
  let node = el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const { overflowX, overflowY } = window.getComputedStyle(node);
    if (/auto|scroll|hidden/.test(overflowX) || /auto|scroll|hidden/.test(overflowY)) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/** Is `rect` a usable box at all? A detached or display:none node measures zero. */
const usable = (rect) => Boolean(rect) && rect.width > 0;

/**
 * Where to draw the overlay, clipped to `clipRect`.
 *
 * @param {DOMRect|null} anchorRect  the collapsed field's current rect
 * @param {DOMRect|null} clipRect    the scroll container's rect, or null for none
 * @returns {{top: number, left: number, width: number}|null}
 *          null means "do not draw" — the anchor has been scrolled out of the
 *          container, and the caller should collapse rather than park the
 *          overlay somewhere the field no longer is.
 */
export function clampOverlayRect(anchorRect, clipRect) {
  if (!usable(anchorRect)) return null;
  if (!usable(clipRect)) {
    return { top: anchorRect.top, left: anchorRect.left, width: anchorRect.width };
  }

  // Vertical: the row itself has scrolled out of the container. Clamping would
  // draw the overlay over whatever is there now, which is how it ended up on
  // top of the header row.
  if (anchorRect.bottom <= clipRect.top || anchorRect.top >= clipRect.bottom) return null;

  const left = Math.max(anchorRect.left, clipRect.left);
  const right = Math.min(anchorRect.right, clipRect.right);
  const width = right - left;

  // Scrolled fully out sideways, or clipped to a sliver too narrow to read.
  if (width < 24) return null;

  return { top: anchorRect.top, left, width };
}

/**
 * Measure an anchor and clamp it in one step.
 *
 * @param {Element|null} anchor
 * @returns {{top: number, left: number, width: number}|null}
 */
export function overlayRectFor(anchor) {
  if (!anchor) return null;
  const clip = findClipAncestor(anchor);
  return clampOverlayRect(anchor.getBoundingClientRect(), clip ? clip.getBoundingClientRect() : null);
}

export default overlayRectFor;
